/**
 * Tax ID validation utilities for country-specific formats.
 * Supports Brazil (CNPJ) and Chile (RUT).
 * For CO/AR/EC, format regex from countryConfig is sufficient.
 */

/**
 * Strips visual formatting separators from a tax ID value.
 * Removes dots, dashes, and slashes — keeping digits and letters.
 *
 * Examples:
 *   '11.222.333/0001-81' → '11222333000181'
 *   '12.345.678-K'       → '12345678K'
 *   '123456789-5'        → '1234567895'
 */
export const stripTaxIdFormatting = (value) => {
  if (!value) return '';
  return value.replace(/[./-]/g, '');
};

/**
 * Validates a Brazilian CNPJ using the modulo-11 double check-digit algorithm.
 * Input must already have visual separators stripped (14 digits).
 * @param {string} value - Raw CNPJ string (digits only, 14 chars)
 * @returns {boolean}
 */
export const validateCNPJ = (value) => {
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 14) return false;
  if (/^(\d)\1+$/.test(digits)) return false; // all same digit

  const calc = (length, multiplierStart) => {
    let sum = 0;
    let multiplier = multiplierStart;
    for (let i = 0; i < length; i++) {
      sum += parseInt(digits[i]) * multiplier--;
      if (multiplier < 2) multiplier = 9;
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const d1 = calc(12, 5);
  if (parseInt(digits[12]) !== d1) return false;

  const d2 = calc(13, 6);
  return parseInt(digits[13]) === d2;
};

/**
 * Validates a Chilean RUT using the modulo-11 check-digit algorithm.
 * Handles check digit values 0-9 and K.
 * Input can have dots stripped but dash between body and check digit is optional.
 *
 * Examples of valid stripped inputs: '76354771K', '123456789'
 * @param {string} value - RUT string (dots already stripped)
 * @returns {boolean}
 */
export const validateRUT = (value) => {
  const cleaned = String(value).replace(/\./g, '').replace(/-/g, '');
  if (cleaned.length < 2) return false;

  const body = cleaned.slice(0, -1);
  const checkChar = cleaned.slice(-1).toLowerCase();

  if (!/^\d+$/.test(body)) return false;

  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = 11 - (sum % 11);
  let expected;
  if (remainder === 11) expected = '0';
  else if (remainder === 10) expected = 'k';
  else expected = String(remainder);

  return checkChar === expected;
};

/**
 * Dispatches to the appropriate country-specific validator.
 * @param {string} rawValue - Tax ID with visual separators already stripped
 * @param {string} countryCode - ISO 3166-1 alpha-2 (e.g., 'BR', 'CL')
 * @returns {boolean}
 */
export const validateTaxId = (rawValue, countryCode) => {
  if (!rawValue) return false;
  switch (countryCode) {
    case 'BR':
      return validateCNPJ(rawValue);
    case 'CL':
      return validateRUT(rawValue);
    default:
      // CO, AR, EC: format regex from countryConfig is sufficient
      return true;
  }
};
